
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight, MessageSquare, Heart, Share2, BookmarkIcon, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks/use-user";
import { Navigation } from "@/components/Navigation";
import { MobileNavigation } from "@/components/MobileNavigation";
import { useAutoScroll } from "@/hooks/use-auto-scroll";
import { toast } from "sonner";
import { useMediaQuery } from "@/hooks/use-media-query";
import { PanelReader } from "@/components/webtoon/PanelReader";
import { usePanelDetection } from "@/hooks/use-panel-detection";
import { usePanelReader } from "@/hooks/use-panel-reader";

// Dados hardcoded dos webtoons (mesmo que em WebtoonDetails.tsx)
const webtoonData = {
  "1": {
    id: 1,
    title: "T-Hunters",
    cover: "/lovable-uploads/coverthunter.webp",
    capa: "/lovable-uploads/b146e022-1fe8-40a4-861a-db11d4d1bc90.png",
    author: "Israel Guedes",
    genre: "Fantasy",
    description: "Para Ken'Ichi, já era difícil tentar ter uma vida normal seguindo os ensinamentos deixados por alguém importante para ele. E ainda tem que lidar com os problemas trazidos pela rixa entre duas organizações secretas: T-Hunters e Caçadores de Recompensas. Será muito azar? Ou tal situação o ajudará a resolver seus conflitos?",
    tags: ["Aventura", "Ação", "Drama"],
    rating: 4.8,
    views: 45678,
    releaseDate: "há cerca de 1 ano",
    episodes: [
      { id: 1, title: "Episode 1", thumbnail: "/lovable-uploads/581526663211.webp", date: "Apr 05, 2021", locked: false, images: ["/lovable-uploads/008.jpg", "/lovable-uploads/009.jpg", "/lovable-uploads/010.jpg"] },
      { id: 2, title: "Episode 2", thumbnail: "/lovable-uploads/3101535418769.webp", date: "Apr 12, 2021", locked: false, images: ["/lovable-uploads/011.jpg", "/lovable-uploads/012.jpg"] },
      { id: 3, title: "Episode 3", thumbnail: "/lovable-uploads/971526785490.webp", date: "Apr 19, 2021", locked: false, images: ["/lovable-uploads/008.jpg", "/lovable-uploads/009.jpg"] },
      { id: 4, title: "Episode 4", thumbnail: "/lovable-uploads/951526762933.webp", date: "Apr 26, 2021", locked: true, images: [] },
    ]
  },
  "2": {
    id: 2,
    title: "Solo Leveling",
    cover: "/lovable-uploads/0f4f44ac-af25-4eb7-aebe-ec2f2302f350.png",
    capa: "/lovable-uploads/e50af48f-7bd6-433c-b3ae-4737b720d1bb_z.png",
    author: "Dubu (Redice Studio)",
    genre: "Action",
    description: "Dez anos atrás, portais começaram a aparecer por todo o mundo, conectando-o a dungeons repletas de monstros. Alguns humanos receberam poderes e foram chamados de 'Caçadores', responsáveis por combater essas criaturas. Sung Jin-Woo é o mais fraco dos Caçadores Rank-E, conhecido como 'O Mais Fraco do Mundo'. Um dia, durante uma masmorra dupla extremamente perigosa, ele se encontra em uma situação de vida ou morte e recebe um estranho poder...",
    tags: ["Action", "Adventure", "Fantasy", "Supernatural", "Martial Arts"],
    rating: 4.9,
    views: 45678,
    releaseDate: "há cerca de 1 ano",
    episodes: [
      { id: 1, title: "Episode 1", thumbnail: "/lovable-uploads/2804a12c-0da4-4a04-a446-800177214eb0.png", date: "Apr 05, 2021", locked: false, images: ["/lovable-uploads/008.jpg", "/lovable-uploads/009.jpg"] },
      { id: 2, title: "Episode 2", thumbnail: "/lovable-uploads/63846342-f428-4efe-9077-1ec6017efdf9.png", date: "Apr 12, 2021", locked: false, images: ["/lovable-uploads/010.jpg", "/lovable-uploads/011.jpg"] },
      { id: 3, title: "Episode 3", thumbnail: "/lovable-uploads/d0461451-fd2c-4fbd-b822-7b3e72ae0d1a.png", date: "Apr 19, 2021", locked: false, images: ["/lovable-uploads/012.jpg"] },
      { id: 4, title: "Episode 4", thumbnail: "/lovable-uploads/0f4f44ac-af25-4eb7-aebe-ec2f2302f350.png", date: "Apr 26, 2021", locked: true, images: [] },
      { id: 5, title: "Episode 5", thumbnail: "/lovable-uploads/4ef64414-f30b-4d5b-babd-31699f267428.png", date: "May 03, 2021", locked: true, images: [] },
    ]
  },
};

const WebtoonEpisodeReader = () => {
  const { id, episodeId } = useParams<{ id: string; episodeId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [webtoon, setWebtoon] = useState<any>(null);
  const [episode, setEpisode] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [episodeImages, setEpisodeImages] = useState<string[]>([]);
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  const {
    scrollRef,
    isAtBottom,
    autoScrollEnabled,
    scrollToBottom,
    disableAutoScroll,
  } = useAutoScroll({
    smooth: true,
    content: episodeImages
  });

  // Panel detection and reader hooks
  const { panels, isLoading: panelsLoading } = usePanelDetection(episodeImages);
  const {
    currentPanelIndex,
    zoomMode,
    enterZoomMode,
    exitZoomMode,
    goToNextPanel,
    goToPrevPanel
  } = usePanelReader({
    panels,
    onNavigateToNextEpisode: () => {
      const nextEpisodeId = parseInt(episodeId || "1") + 1;
      const nextEpisode = webtoon?.episodes?.find((ep: any) => ep.id === nextEpisodeId);
      if (nextEpisode && !nextEpisode.locked) {
        navigate(`/webtoon/${id}/episode/${nextEpisodeId}`);
      }
    },
    onNavigateToPrevEpisode: () => {
      const prevEpisodeId = Math.max(1, parseInt(episodeId || "1") - 1);
      navigate(`/webtoon/${id}/episode/${prevEpisodeId}`);
    }
  });

  useEffect(() => {
    const loadWebtoonAndEpisode = async () => {
      try {
        setIsLoading(true);
        
        // Buscar webtoon pelos dados hardcoded
        const webtoonFound = webtoonData[id as keyof typeof webtoonData];
        
        if (!webtoonFound) {
          console.error("Webtoon não encontrado para ID:", id);
          setIsLoading(false);
          return;
        }

        // Buscar episódio específico
        const episodeFound = webtoonFound.episodes.find(
          (ep) => ep.id === parseInt(episodeId || "1")
        );

        if (!episodeFound) {
          console.error("Episódio não encontrado para ID:", episodeId);
          setIsLoading(false);
          return;
        }

        if (episodeFound.locked) {
          toast.error("Este episódio está bloqueado");
          navigate(`/webtoon/${id}`);
          return;
        }

        setWebtoon(webtoonFound);
        setEpisode(episodeFound);
        setEpisodeImages(episodeFound.images || []);

        // Verificar likes/bookmarks do usuário se logado
        if (user?.id) {
          // Simular verificação de likes
          setIsLiked(false);
          setIsBookmarked(false);
        }

      } catch (error) {
        console.error("Erro ao carregar webtoon/episódio:", error);
        toast.error("Erro ao carregar o episódio");
      } finally {
        setIsLoading(false);
      }
    };

    if (id && episodeId) {
      loadWebtoonAndEpisode();
    }
  }, [id, episodeId, user?.id, navigate]);

  const handleToggleLike = async () => {
    if (!user) {
      toast.error("Faça login para curtir este episódio");
      return;
    }

    // Simular toggle de like
    setIsLiked(!isLiked);
    toast.success(isLiked ? "Curtida removida" : "Episódio curtido!");
  };

  const handleToggleBookmark = async () => {
    if (!user) {
      toast.error("Faça login para favoritar este episódio");
      return;
    }

    // Simular toggle de bookmark
    setIsBookmarked(!isBookmarked);
    toast.success(isBookmarked ? "Removido dos favoritos" : "Adicionado aos favoritos");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!webtoon || !episode) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container max-w-3xl mx-auto px-4 pt-20">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">Episódio não encontrado</h1>
            <p className="text-muted-foreground mb-6">
              O episódio que você está procurando não existe ou não está disponível.
            </p>
            <Button onClick={() => navigate(`/webtoon/${id}`)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Voltar para Webtoon
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Reader */}
      <div className="container relative max-w-3xl mx-auto px-4 pt-6 pb-24">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/webtoon/${id}`)}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant={isLiked ? "default" : "outline"}
              size="sm"
              onClick={handleToggleLike}
            >
              <Heart className={`h-4 w-4 ${isLiked ? "fill-current" : ""} mr-1`} />
              <span>Curtir</span>
            </Button>
            
            <Button
              variant={isBookmarked ? "default" : "outline"}
              size="sm"
              onClick={handleToggleBookmark}
            >
              <BookmarkIcon className={`h-4 w-4 ${isBookmarked ? "fill-current" : ""} mr-1`} />
              <span>Salvar</span>
            </Button>
          </div>
        </div>
        
        <h1 className="text-2xl font-bold mb-2">{webtoon.title}</h1>
        <h2 className="text-lg font-medium mb-2">{episode.title}</h2>
        <p className="text-muted-foreground mb-6">{webtoon.description}</p>
        
        {/* Episode Reader with Panel Detection */}
        {episodeImages.length > 0 ? (
          <PanelReader
            panels={panels}
            images={episodeImages}
            currentPanelIndex={currentPanelIndex}
            zoomMode={zoomMode}
            onPanelClick={enterZoomMode}
            onExitZoom={exitZoomMode}
            onNextPanel={goToNextPanel}
            onPrevPanel={goToPrevPanel}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              Nenhuma imagem disponível para este episódio.
            </p>
          </div>
        )}
        
        {/* Navigation between episodes */}
        <div className="flex justify-between items-center mt-8 pt-8 border-t">
          <Button
            variant="outline"
            onClick={() => {
              const prevEpisodeId = Math.max(1, parseInt(episodeId || "1") - 1);
              navigate(`/webtoon/${id}/episode/${prevEpisodeId}`);
            }}
            disabled={parseInt(episodeId || "1") <= 1}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Episódio Anterior
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              const nextEpisodeId = parseInt(episodeId || "1") + 1;
              const nextEpisode = webtoon.episodes?.find((ep: any) => ep.id === nextEpisodeId);
              if (nextEpisode && !nextEpisode.locked) {
                navigate(`/webtoon/${id}/episode/${nextEpisodeId}`);
              }
            }}
            disabled={(() => {
              const nextEpisodeId = parseInt(episodeId || "1") + 1;
              const nextEpisode = webtoon.episodes?.find((ep: any) => ep.id === nextEpisodeId);
              return !nextEpisode || nextEpisode.locked;
            })()}
          >
            Próximo Episódio
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
        
        {!isAtBottom && episodeImages.length > 0 && (
          <Button
            onClick={() => scrollToBottom()}
            size="icon"
            variant="outline"
            className="fixed bottom-20 right-4 inline-flex rounded-full shadow-md z-10"
            aria-label="Scroll to bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <MobileNavigation />
    </div>
  );
};

export default WebtoonEpisodeReader;
